import last from 'lodash/last';
import uuid from 'uuid';

import { FileApi } from '@api/file-api';

const MAX_IMAGE_SIZE = 800;

export async function loadAvatar(file: File, userId?: string): Promise<string> {
    const fileName = makeFileName(file.name, userId);
    const blob = await resizeImage(file);
    const resizedFile = new File([blob], fileName, { type: blob.type });

    await FileApi.upload(resizedFile, { fileName, immutable: true });
    return FileApi.makeSharedLink(fileName);
}

function makeFileName(name: string, userId?: string): string {
    const ext = last(name.split('.'));
    return `avatars/${userId ?? uuid()}.${ext}`;
}

async function resizeImage(file: File): Promise<Blob> {
    const image = await loadImage(await readAsDataUrl(file));

    if (image.width <= MAX_IMAGE_SIZE && image.height <= MAX_IMAGE_SIZE) {
        return file;
    }

    return drawScaledImage(image, file.type);
}

function readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load image'));
        image.src = src;
    });
}

function drawScaledImage(image: HTMLImageElement, type: string): Promise<Blob> {
    const canvas = document.createElement('canvas');
    const scale = Math.min(MAX_IMAGE_SIZE / image.width, MAX_IMAGE_SIZE / image.height);
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Failed to encode image'))), type, 1);
    });
}
